<template>
  <div>
    <v-layout v-if="loading">
      <v-progress-linear color="primary" indeterminate></v-progress-linear>
    </v-layout>
    <gridLayout
      :layout.sync="layout"
      :col-num="12"
      :row-height="rowHeight"
      :is-draggable="dashboardEditMode"
      :is-resizable="dashboardEditMode"
      :vertical-compact="true"
      :responsive="true"
      @layout-updated="onLayoutUpdated"
      :margin="[10, 2]"
      :useCssTransforms="false"
      :autoSize="true"
    >
      <gridItem
        v-for="item in layout"
        :x="item.x"
        :y="item.y"
        :w="item.w"
        :h="item.h"
        :i="item.i"
        :minW="item.minW"
        :minH="item.minH"
        @resized="onResized"
        :key="item.id"
      >
        <WidgetListItem
          :ref="`widget-item-${item.i}`"
          v-if="isListItem(item.widgetId)"
          :height="widgetHeight(item.h)"
          :width="item.w"
          :widgetId="item.widgetId"
          :id="item.id"
          :settings="item.settings"
          @on-remove-widget="onRemoveWidget"
          :enableEdit="dashboardEditMode"
        />

        <WidgetSearchHistory
          :ref="`widget-item-${item.i}`"
          v-if="isSearchHistory(item.widgetId)"
          :height="widgetHeight(item.h)"
          :width="item.w"
          :widgetId="item.widgetId"
          :id="item.id"
          :settings="item.settings"
          @on-remove-widget="onRemoveWidget"
          :enableEdit="dashboardEditMode"
        />

        <WidgetLineChart
          :ref="`widget-item-${item.i}`"
          v-if="isLineChart(item.widgetId)"
          :height="widgetHeight(item.h)"
          :width="item.w"
          :widgetId="item.widgetId"
          :id="item.id"
          :settings="item.settings"
          @on-remove-widget="onRemoveWidget"
          :enableEdit="dashboardEditMode"
        />

        <WidgetPieChart
          :ref="`widget-item-${item.i}`"
          v-if="isPieChart(item.widgetId)"
          :height="widgetHeight(item.h)"
          :width="item.w"
          :widgetId="item.widgetId"
          :id="item.id"
          @on-remove-widget="onRemoveWidget"
          :enableEdit="dashboardEditMode"
        />

        <WidgetBookmark
          :ref="`widget-item-${item.i}`"
          v-if="isBookmark(item.widgetId)"
          :height="widgetHeight(item.h)"
          :width="item.w"
          :widgetId="item.widgetId"
          :id="item.id"
          :settings="item.settings"
          @on-remove-widget="onRemoveWidget"
          :enableEdit="dashboardEditMode"
        />

        <WidgetNote
          :ref="`widget-item-${item.i}`"
          v-if="isNote(item.widgetId)"
          :height="widgetHeight(item.h)"
          :width="item.w"
          :id="item.id"
          :settings="item.settings"
          @on-remove-widget="onRemoveWidget"
          :enableEdit="dashboardEditMode"
        />

        <WidgetNewsfeed
          :ref="`widget-item-${item.i}`"
          v-if="item.widgetId == widgetType.Newsfeed"
          :height="widgetHeight(item.h)"
          :width="item.w"
          :widgetId="item.widgetId"
          :id="item.id"
          :settings="item.settings"
          @on-remove-widget="onRemoveWidget"
          :enableEdit="dashboardEditMode"
        />

        <WidgetDocumentTracker
          :ref="`widget-item-${item.i}`"
          v-if="item.widgetId == widgetType.DocumentTracker"
          :height="widgetHeight(item.h)"
          :width="item.w"
          :widgetId="item.widgetId"
          :id="item.id"
          :settings="item.settings"
          @on-remove-widget="onRemoveWidget"
          :enableEdit="dashboardEditMode"
        />

        <WidgetBasket
          :ref="`widget-item-${item.i}`"
          v-if="item.widgetId == widgetType.Baskets"
          :height="widgetHeight(item.h)"
          :width="item.w"
          :widgetId="item.widgetId"
          :id="item.id"
          :settings="item.settings"
          @on-remove-widget="onRemoveWidget"
          :enableEdit="dashboardEditMode"
        />
        <WidgetAutorecognitionTracker
        :ref="`widget-item-${item.i}`"
          v-if="item.widgetId == widgetType.AutorecognitionTracker"
          :height="widgetHeight(item.h)"
          :width="item.w"
          :widgetId="item.widgetId"
          :id="item.id"
          :settings="item.settings"
          @on-remove-widget="onRemoveWidget"
          :enableEdit="dashboardEditMode"
        />
      </gridItem>
    </gridLayout>
    <WidgetAddModal
      :ref="widgetModalKey"
      @on-widget-selected="onWidgetSelected"
    />

    <WidgetImportSettings
      :ref="widgetImportModalKey"
      @on-saved="onWidgetImportSaved"
    />

    <v-fab-transition>
      <v-tooltip left>
        <template v-slot:activator="{ on }">
          <v-btn
            v-show="dashboardEditMode"
            color="success"
            v-on="on"
            fab
            fixed
            :loading="loading"
            dark
            bottom
            right
            :class="speedDialPosition"
            @click="saveLayout"
          >
            <v-icon>mdi-content-save</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Save") }}</span>
      </v-tooltip>
    </v-fab-transition>

    <v-speed-dial
        v-show="!canEditDashboard"
        v-model="fab"
        fixed
        :class="speedDialPosition"
        bottom
        right
        :direction="'top'"
        :transition="'slide-y-reverse-transition'">
      <template v-slot:activator>
        <v-tooltip left dark fab>
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="primary" dark fab v-bind="attrs" v-on="on">
              <v-icon> mdi-widgets </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("YouDoNotHavePermissionToEditDashboard") }}</span>
        </v-tooltip>
      </template>
    </v-speed-dial>

    <v-speed-dial
      v-show="canEditDashboard && !dashboardEditMode"
      v-model="fab"
      fixed
      :class="speedDialPosition"
      bottom
      right
      :direction="'top'"
      :open-on-hover="true"
      :transition="'slide-y-reverse-transition'"
    >
      <template v-slot:activator>
        <v-btn v-model="fab" color="primary" dark fab>
          <v-icon v-if="fab"> mdi-close </v-icon>
          <v-icon v-else> mdi-widgets </v-icon>
        </v-btn>
      </template>
      <v-tooltip left>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            fab
            dark
            small
            color="warning"
            @click.stop="dashboardEditMode = true"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Edit") }}</span>
      </v-tooltip>
      <v-tooltip left>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            fab
            dark
            small
            color="success"
            @click="showWidgetList"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Add") }}</span>
      </v-tooltip>
      <v-tooltip left>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            v-if="canImport"
            fab
            dark
            small
            color="error"
            @click="importDashboard"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
        <span>{{ isAdmin ? $t("Export") : $t("Import") }}</span>
      </v-tooltip>
    </v-speed-dial>
  </div>
</template>

<script>
import VueGridLayout from 'vue-grid-layout';
import Widget from '@/ecm/models/Widget';
import WidgetType from '@/ecm/variables/WidgetType';
import WidgetListItem from '@/components/widgets/WidgetListItem';
import WidgetLineChart from '@/components/widgets/WidgetLineChart';
import WidgetPieChart from '@/components/widgets/WidgetPieChart';
import WidgetAddModal from '@/components/widgets/WidgetAddModal';
import WidgetSearchHistory from '@/components/widgets/WidgetSearchHistory';
import WidgetBookmark from '@/components/widgets/WidgetBookmark';
import WidgetNote from '@/components/widgets/note/WidgetNote';
import WidgetNewsfeed from '@/components/widgets/newsfeed/WidgetNewsfeed';
import WidgetDocumentTracker from '@/components/widgets/documentTracker/WidgetDocumentTracker';
import WidgetImportSettings from '@/components/widgets//WidgetImportSettings';
import WidgetBasket from '@/components/widgets/basket/WidgetBasket';
import { EventBus } from '@/eventBus.js';
import Utils from '@/ecm/common/Utils.js';
import WidgetAutorecognitionTracker from '@/components/widgets/autorecognition/WidgetAutorecognitionTracker';
import UserWidgetService from '@/services/UserWidgetService';

const WIDGET_SIZES = {
    [WidgetType.Baskets]: { w: 2, h: 2 },
    [WidgetType.UserSearches]: { w: 2, h: 2 },
    [WidgetType.Distributions]: { w: 2, h: 2 },
    [WidgetType.UploadedDocumentsChart]: { w: 3, h: 6 },
    [WidgetType.DocumentsByContentTypeChart]: { w: 3, h: 8 },
    [WidgetType.DocumentsByWorkflowChart]: { w: 3, h: 8 },
    [WidgetType.Bookmark]: { w: 2, h: 2 },
    [WidgetType.Note]: { w: 2, h: 3 },
    [WidgetType.SearchHistory]: { w: 2, h: 2 },
    [WidgetType.Newsfeed]: { w: 2, h: 2 },
    [WidgetType.DocumentTracker]: { w: 3, h: 3 },
    [WidgetType.AutorecognitionTracker]: { w: 3, h: 6 },
};

export default {
    components: {
        GridLayout: VueGridLayout.GridLayout,
        GridItem: VueGridLayout.GridItem,
        WidgetAddModal,
        WidgetListItem,
        WidgetPieChart,
        WidgetLineChart,
        WidgetBookmark,
        WidgetNote,
        WidgetSearchHistory,
        WidgetNewsfeed,
        WidgetDocumentTracker,
        WidgetImportSettings,
        WidgetBasket,
        WidgetAutorecognitionTracker
    },
    data: () => ({
        loading: false,
        layout: [],
        updatedLayout: [],
        rowHeight: 40,
        widgetModalKey: 'widgetListModal',
        widgetImportModalKey: 'widgetImportModal',
        fab: false,
        dashboardEditMode: false,
        canEditDashboard: true,
        scrolledToBottom: true,
        widgetType: WidgetType
    }),
    mounted() {
        this.loadWidgets();
        this.scroll();

        this.canEditDashboard = this.$store.getters['account/user'].CanEditDashboard;

        EventBus.$on('addWidget', () => this.showWidgetList());
    },
    destroyed() {
        EventBus.$off('addWidget');
    },
    computed: {
        widgetListModal() {
            return this.$refs[this.widgetModalKey];
        },
        widgetImportModal() {
            return this.$refs[this.widgetImportModalKey];
        },
        speedDialPosition() {
            if (this.scrolledToBottom) {
                return 'mb-14';
            } else {
                return '';
            }
        },
        canImport() {
            return this.$store.getters['account/user'].AllowImportDashboard;
        },
        isAdmin() {
            return this.$store.getters['account/user'] && this.$store.getters['account/user'].SysLevel === 2;
        },
    },
    methods: {
        scroll() {
            window.onscroll = () => {
                this.isAtBottom();
            };
        },
        isAtBottom() {
            const bottomOfWindow =
                Math.max(
                    window.pageYOffset,
                    document.documentElement.scrollTop,
                    document.body.scrollTop
                ) +
                window.innerHeight ===
                document.documentElement.offsetHeight;

            if (bottomOfWindow) {
                this.scrolledToBottom = true; // replace it with your code
            } else {
                this.scrolledToBottom = false;
            }
        },
        widgetHeight(itemH) {
            return itemH * this.rowHeight + itemH - 2;
        },
        onResized(widgetId, newH, newW, newHPx, newWPx) {
            this.$refs[`widget-item-${widgetId}`][0].resize(
                this.widgetHeight(newH),
                newWPx
            );
        },
        onLayoutUpdated(newLayout) {
            this.updatedLayout = newLayout;
        },
        onRemoveWidget(id) {
            this.$confirm(this.$t('ItemDeleteConfirmation'), {
                title: this.$t('Delete'),
                icon: 'mdi-alert',
                color: 'red',
                buttonTrueText: this.$t('Delete'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res == 1) {
                    var widget = this.layout.filter((i) => i.id == id)[0];
                    this.layout.splice(this.layout.indexOf(widget), 1);
                    UserWidgetService.delete(id);
                }
            });
        },
        loadWidgets() {
            UserWidgetService.getForUser().then((data) => {
                const widgetLayout = [];

                for (let i = 0; i < data.length; i++) {
                    widgetLayout.push(new Widget({ ...data[i], Index: i }));
                }

                this.layout = widgetLayout;
                this.getMinWidthHeight();
            });
        },
        getMinWidthHeight() {
            this.layout.forEach(w => {
                const { w: minW, h: minH } = WIDGET_SIZES[w.widgetId] || { w: 2, h: 2 };
                w.minW = minW;
                w.minH = minH;
            });
        },
        getDefaultWidthHeight(widgetId) {
            return WIDGET_SIZES[widgetId] || { w: 2, h: 2 };
        },
        saveLayout() {
            this.isAtBottom();

            var layout = this.updatedLayout.map((w) =>
                Object.assign(new Widget(), w).toDto()
            );

            if (layout.length == 0) {
                this.dashboardEditMode = false;
                return;
            }
            this.loading = true;
            UserWidgetService.updateLayout(layout)
                .then(() => {
                    Utils.toast.success(this.$t('DashboardSaved'));
                    this.dashboardEditMode = false;
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.loading = false;
                });
        },
        showWidgetList() {
            this.widgetListModal.show();
        },
        async onWidgetSelected(widgetIds) {
            this.loading = true;
            for (let index = 0; index < widgetIds.length; index++) {
                const widgetId = widgetIds[index];
                let y = Math.max(...this.layout.map((l) => l.y));
                if (!isFinite(y)) {
                    y = 0;
                }
                const widget = new Widget();
                widget.id = 0;
                widget.widgetId = parseInt(widgetId);
                widget.x = 0;
                widget.y = y + 1;
                const { w, h } = this.getDefaultWidthHeight(widgetId);
                widget.w = w;
                widget.h = h;
                widget.i = this.layout.length + 1;
                var newId = await UserWidgetService.add(widget.toDto());
                widget.id = newId;
                this.layout.push(widget);
            }
            this.getMinWidthHeight();
            this.$nextTick(() => {
                const ref = 'widget-item-' + this.layout.length;
                this.$vuetify.goTo(this.$refs[ref][0], {
                    duration: 1000,
                    offset: 0,
                    easing: 'easeInOutQuart',
                });
            });
            this.dashboardEditMode = true;
            this.loading = false;
        },
        isListItem(widgetId) {
            return (
                widgetId == WidgetType.UserSearches ||
                widgetId == WidgetType.Distributions
            );
        },
        isSearchHistory(widgetId) {
            return widgetId == WidgetType.SearchHistory;
        },
        isLineChart(widgetId) {
            return widgetId == WidgetType.UploadedDocumentsChart;
        },
        isPieChart(widgetId) {
            return (
                widgetId == WidgetType.DocumentsByContentTypeChart ||
                widgetId == WidgetType.DocumentsByWorkflowChart
            );
        },
        isBookmark(widgetId) {
            return widgetId == WidgetType.Bookmark;
        },
        isNote(widgetId) {
            return widgetId == WidgetType.Note;
        },
        isImage(widgetId) {
            return widgetId == WidgetType.Card;
        },
        importDashboard() {
            this.widgetImportModal.show();
        },
        onWidgetImportSaved() {
            this.loadWidgets();
            this.$store.dispatch('account/getUserSavedSearches');
        },
    },
};
</script>
